import { Link } from "@tanstack/react-router"
import { Ban, BellRing, Eye } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { cn } from "@holigral/shared/ui/lib/utils"
import {
  ConfirmDialog,
  IconButton,
  Tooltip,
} from "@holigral/shared/ui/composed"
import AssignmentDetailDialog from "./AssignmentDetailDialog"
import { AssignmentStatus } from "./model"
import type { TenantAssignment } from "./model"
import { useRemindAssignmentMutation, useRevokeAssignmentMutation } from "./api"
import { useT } from "@/i18n"
import { useCurrentTenant } from "@/tenant"

type Props = {
  readonly assignment: TenantAssignment
}

export default function AssignmentRowActions({ assignment }: Props) {
  const { t } = useT("assignment")
  const { tenantSlug } = useCurrentTenant()
  const [detailOpen, setDetailOpen] = useState(false)
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false)
  const [revokeAssignment] = useRevokeAssignmentMutation()
  const [remindAssignment, { isLoading: isReminding }] =
    useRemindAssignmentMutation()

  const isActive =
    assignment.status === AssignmentStatus.Pending ||
    assignment.status === AssignmentStatus.InProgress

  const handleRemind = async () => {
    if (!assignment.canRemind || isReminding) {
      return
    }
    await remindAssignment(assignment.id).unwrap()
    toast.success(t("actions.reminded"))
  }

  const handleRevokeConfirmClose = async (confirmed: boolean) => {
    setConfirmRevokeOpen(false)
    if (!confirmed) {
      return
    }
    await revokeAssignment(assignment.id).unwrap()
    toast.success(t("actions.revoked"))
  }

  return (
    <div className="flex justify-end">
      {assignment.status === AssignmentStatus.Evaluated ? (
        <Tooltip tooltip={t("evaluations.view")}>
          <IconButton
            size="icon-sm"
            icon={<Eye />}
            aria-label={t("evaluations.view")}
            nativeButton={false}
            role="link"
            render={
              <Link
                to="/t/$tenantSlug/evaluations/$assignmentId"
                params={{ tenantSlug, assignmentId: assignment.id }}
              />
            }
          />
        </Tooltip>
      ) : (
        <Tooltip tooltip={t("actions.view")}>
          <IconButton
            size="icon-sm"
            icon={<Eye />}
            aria-label={t("actions.view")}
            onClick={() => {
              setDetailOpen(true)
            }}
          />
        </Tooltip>
      )}
      {isActive && (
        // Kept clickable (aria-disabled only) so the lockout hint tooltip
        // still shows; handleRemind no-ops while locked out
        <Tooltip
          tooltip={
            assignment.canRemind
              ? t("actions.remind")
              : t("actions.remindLockout")
          }
        >
          <IconButton
            size="icon-sm"
            icon={<BellRing />}
            aria-label={t("actions.remind")}
            aria-disabled={!assignment.canRemind}
            className={cn(!assignment.canRemind && "opacity-50")}
            onClick={handleRemind}
          />
        </Tooltip>
      )}
      {isActive && (
        <Tooltip tooltip={t("actions.revoke")}>
          <IconButton
            size="icon-sm"
            icon={<Ban />}
            aria-label={t("actions.revoke")}
            onClick={() => {
              setConfirmRevokeOpen(true)
            }}
          />
        </Tooltip>
      )}
      <AssignmentDetailDialog
        assignmentId={assignment.id}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
      <ConfirmDialog
        open={confirmRevokeOpen}
        title={t("actions.revokeConfirmTitle")}
        description={t("actions.revokeConfirmDescription", {
          email: assignment.respondent.email ?? "—",
        })}
        onClose={handleRevokeConfirmClose}
      />
    </div>
  )
}
